import request, { keysToSnakeCase } from "@src/utils/request";
import { PaginationValues } from "@src/types/pagination";
import { SearchResultsFilters } from "@src/components/pages/clinic-portal/SearchResultsPage/SearchResultsPageFilters";
import map from "lodash/map";

export type SearchDistributorItem = {
  distributor: string;
  isPreferred: boolean;
  itemNumber: string;
  pastOrderCount: number;
  pricesAndAvailabilities?: {
    price: number;
    availabilityStatus: string;
    minQuantity?: number;
  }[];
  lastOrderedDetails?: {
    id: string;
    placedAtTimestamp: string;
    pricePerUnit: number;
    unitsOrdered: number;
  } | null;
};

export type SearchProductVariant = {
  id: string;
  itemName: string;
  itemNumber: string;
  unitOfMeasure: string;
  packageQuantity: number;
  variantValues: Record<string, string | number>;
};

export type SearchProductResult = {
  id: string;
  /** Full PDP URL including group_attributes as query params, e.g. /product/123?formulation=Caplets&strength=75mg */
  pdpUrl?: string;
  /** Key-value pairs that distinguish this group from siblings (used to pre-populate PDP chips) */
  groupAttributes?: Record<string, string>;
  /** True when this group has exactly one member — pricing can be shown directly in the tile */
  isStandalone?: boolean;
  itemName: string;
  manufacturer?: { id: string | null; name: string } | null;
  imageUrl?: string;
  thumbnailUrl?: string;
  products: SearchProductVariant[];
  distributorItems: SearchDistributorItem[];
};

export type FacetBucket = {
  key: string;
  count: number;
};

export type SearchFacets = {
  categories?: FacetBucket[];
  manufacturers?: FacetBucket[];
  ingredients?: FacetBucket[];
  distributors?: FacetBucket[];
  inStock?: FacetBucket[];
  hasImages?: FacetBucket[];
};

export type ProductSearchPage = {
  next: string | null;
  previous: string | null;
  totalCount: number;
  totalPages: number;
  page: number;
  results: SearchProductResult[];
  facets?: SearchFacets;
};

export async function searchProducts(
  userId?: string,
  term?: string,
  sortBy?: string,
  paginationFilters?: PaginationValues,
  activeFilters?: SearchResultsFilters,
): Promise<{ body: ProductSearchPage }> {
  // Build request body for Django search endpoint
  const requestBody: Record<string, any> = {
    search: term || "",
    page: paginationFilters?.page || 1,
    page_size: paginationFilters?.pageSize || 20,
    sort_by: sortBy || "_score",
    include_pricing: true,
  };

  // Add active filters if present
  if (activeFilters && Object.keys(activeFilters).length > 0) {
    requestBody.active_filters = keysToSnakeCase(activeFilters);
  }

  return request().post("/api/v1/search/").send(JSON.stringify(requestBody));
}
